import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from '@/components/ui/breadcrumb';
import { toast } from 'sonner';
import { getAppSocket } from '../lib/wesSocket';

interface EmailConfig {
  provider: string;
  smtpHost: string;
  smtpPort: number;
  username: string;
  password: string;
  fromEmail: string;
  fromName: string;
  useTLS: boolean;
  enabled: boolean;
  bccDefault?: string;
  replyToDefault?: string;
}

interface EmailConfigForm {
  provider: string;
  smtpHost: string;
  smtpPort: number;
  username: string;
  password: string;
  fromEmail: string;
  fromName: string;
  useTLS: boolean;
  enabled: boolean;
  bccDefault?: string;
  replyToDefault?: string;
}

const EmailConfigPage: React.FC = () => {
  const navigate = useNavigate();
  const [emailConfig, setEmailConfig] = useState<EmailConfig | null>(null);
  const [testingEmail, setTestingEmail] = useState<string>('');
  const [isTestingEmail, setIsTestingEmail] = useState(false);
  const [emailTestResult, setEmailTestResult] = useState<{ success: boolean; message: string; details?: any } | null>(null);
  const [emailConfigLoading, setEmailConfigLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty }
  } = useForm<EmailConfigForm>({
    defaultValues: {
      provider: 'custom',
      smtpHost: '',
      smtpPort: 587,
      username: '',
      password: '',
      fromEmail: '',
      fromName: '',
      useTLS: true,
      enabled: false,
      bccDefault: '',
      replyToDefault: ''
    }
  });

  const watchedProvider = watch('provider');

  useEffect(() => {
    fetchEmailConfig();
  }, []);

  useEffect(() => {
    if (emailConfig) {
      loadEmailConfigIntoForm();
    }
  }, [emailConfig]);

  const realtimeTimerRef = useRef<number | null>(null);
  const pendingRefreshRef = useRef(false);
  const notifiedRef = useRef(false);
  useEffect(() => {
    const socket = getAppSocket();

    const scheduleSilentRefresh = (payload?: any) => {
      const path = String(payload?.path || '');
      if (path && !(path.startsWith('/api/email-config') || path.startsWith('/api/settings'))) {
        return;
      }

      if (isDirty || emailConfigLoading || loading) {
        pendingRefreshRef.current = true;
        if (!notifiedRef.current && isDirty) {
          notifiedRef.current = true;
          toast.message('Se detectaron cambios externos. Se actualizará al guardar o descartar cambios.');
        }
        return;
      }

      if (realtimeTimerRef.current) window.clearTimeout(realtimeTimerRef.current);
      realtimeTimerRef.current = window.setTimeout(() => {
        fetchEmailConfig({ silent: true });
      }, 450);
    };

    socket.on('area:update', scheduleSilentRefresh);
    socket.on('connect', scheduleSilentRefresh);

    return () => {
      if (realtimeTimerRef.current) window.clearTimeout(realtimeTimerRef.current);
      socket.off('area:update', scheduleSilentRefresh);
      socket.off('connect', scheduleSilentRefresh);
    };
  }, [isDirty, emailConfigLoading, loading]);

  useEffect(() => {
    if (isDirty || emailConfigLoading || loading) return;
    if (!pendingRefreshRef.current) return;
    pendingRefreshRef.current = false;
    notifiedRef.current = false;
    fetchEmailConfig({ silent: true });
  }, [isDirty, emailConfigLoading, loading]);

  const fetchEmailConfig = async (options?: { silent?: boolean }) => {
    try {
      const response = await axios.get('/api/email-config');
      if (response.data) {
        setEmailConfig(response.data);
      }
    } catch (error: any) {
      console.log('No hay configuración de email guardada o error:', error.message);
    }
  };

  const loadEmailConfigIntoForm = () => {
    if (emailConfig) {
      console.log('📧 Cargando configuración en formulario:', emailConfig);
      setValue('provider', emailConfig.provider || 'custom');
      setValue('smtpHost', emailConfig.smtpHost || '');
      setValue('smtpPort', emailConfig.smtpPort ? Number(emailConfig.smtpPort) : 587);
      setValue('username', emailConfig.username || '');
      setValue('password', emailConfig.password || '');
      setValue('fromEmail', emailConfig.fromEmail || '');
      setValue('fromName', emailConfig.fromName || '');
      setValue('useTLS', Boolean(emailConfig.useTLS));
      setValue('enabled', Boolean(emailConfig.enabled));
      setValue('bccDefault', emailConfig.bccDefault || '');
      setValue('replyToDefault', emailConfig.replyToDefault || '');
    }
  };

  const onSubmit = async (data: EmailConfigForm) => {
    try {
      if (!data.provider) {
        toast.error('Debes seleccionar un proveedor de email');
        return;
      }

      setEmailConfigLoading(true);

      const backendData: any = {
        provider: data.provider,
        smtpHost: data.smtpHost,
        smtpPort: Number(data.smtpPort),
        username: data.username,
        password: data.password,
        fromEmail: data.fromEmail,
        fromName: data.fromName,
        useTLS: Boolean(data.useTLS),
        enabled: Boolean(data.enabled),
        bccDefault: data.bccDefault || '',
        replyToDefault: data.replyToDefault || ''
      };

      const response = await axios.post('/api/email-config', backendData);

      const newConfig: EmailConfig = {
        provider: data.provider,
        smtpHost: data.smtpHost,
        smtpPort: Number(data.smtpPort),
        username: data.username,
        password: data.password,
        fromEmail: data.fromEmail,
        fromName: data.fromName,
        useTLS: Boolean(data.useTLS),
        enabled: Boolean(data.enabled),
        bccDefault: data.bccDefault || '',
        replyToDefault: data.replyToDefault || ''
      };

      setEmailConfig(newConfig);
      toast.success('Configuración de email actualizada exitosamente');

      // Refetch to get the latest
      await fetchEmailConfig();
    } catch (error: any) {
      console.error('Error al guardar configuración de email:', error);
      toast.error(error.response?.data?.error || 'Error al actualizar la configuración de email');
    } finally {
      setEmailConfigLoading(false);
    }
  };

  const onSubmitTestingEmail = async () => {
    try {
      setEmailConfigLoading(true);
      console.log('📨 Enviando email de prueba a:', testingEmail);

      const response = await axios.post('/api/email-config/test', { email: testingEmail });

      setEmailTestResult(response.data);
      if (response.data.success) {
        toast.success('Correo de prueba enviado exitosamente');
      } else {
        toast.error(response.data.error || 'Error al enviar correo de prueba');
      }

      setTestingEmail('');
    } catch (error: any) {
      console.error('Error al enviar email de prueba:', error);
      const errorMessage = error.response?.data?.error || 'Error al enviar correo de prueba';
      setEmailTestResult({
        success: false,
        message: errorMessage,
        details: error.response?.data
      });
      toast.error(errorMessage);
    } finally {
      setEmailConfigLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <button onClick={() => navigate('/settings')} className="text-left">
                  Configuración
                </button>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Configuración de Email</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex justify-end">
          <Button variant="outline" onClick={() => navigate('/settings')}>
            Volver
          </Button>
        </div>

        <div className="space-y-6">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">

            {/* Configuración SMTP */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Configuración SMTP</h3>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="smtpHost">Servidor SMTP</Label>
                  <Input
                    id="smtpHost"
                    type="text"
                    {...register('smtpHost')}
                    placeholder="smtp.tu-proveedor.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="smtpPort">Puerto SMTP</Label>
                  <Input
                    id="smtpPort"
                    type="number"
                    {...register('smtpPort')}
                    placeholder="587"
                  />
                </div>
              </div>
            </div>

            {/* Credenciales */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Credenciales</h3>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="username">Usuario/Email *</Label>
                  <Input
                    id="username"
                    type="email"
                    {...register('username', { required: 'El usuario es requerido' })}
                    placeholder="tu@email.com"
                  />
                  {errors.username && (
                    <p className="text-sm text-destructive">{errors.username.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Contraseña/App Password *</Label>
                  <Input
                    id="password"
                    type="password"
                    {...register('password', { required: 'La contraseña es requerida' })}
                    placeholder="Contraseña"
                  />
                  {errors.password && (
                    <p className="text-sm text-destructive">{errors.password.message}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Información del remitente */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Información del Remitente</h3>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="fromEmail">Email del remitente *</Label>
                  <Input
                    id="fromEmail"
                    type="email"
                    {...register('fromEmail', { required: 'El email del remitente es requerido' })}
                    placeholder="noreply@tuempresa.com"
                  />
                  {errors.fromEmail && (
                    <p className="text-sm text-destructive">{errors.fromEmail.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fromName">Nombre del remitente *</Label>
                  <Input
                    id="fromName"
                    type="text"
                    {...register('fromName', { required: 'El nombre del remitente es requerido' })}
                    placeholder="Tu Empresa"
                  />
                  {errors.fromName && (
                    <p className="text-sm text-destructive">{errors.fromName.message}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Copia y respuestas */}
            <div className="space-y-4 pt-6 border-t border-border">
              <div>
                <h3 className="text-lg font-semibold">Copia y respuestas</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Para presupuestos, diagnósticos, remitos y consultas (no para emails internos del sistema).
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="bccDefault">BCC (copia oculta) por defecto</Label>
                  <Input
                    id="bccDefault"
                    type="email"
                    {...register('bccDefault')}
                    placeholder="ventas@rocam.com.ar"
                  />
                  <p className="text-xs text-muted-foreground">
                    Todos los emails comerciales se mandarán con copia oculta a esta dirección.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="replyToDefault">Reply-To (responder a) por defecto</Label>
                  <Input
                    id="replyToDefault"
                    type="email"
                    {...register('replyToDefault')}
                    placeholder="ventas@rocam.com.ar"
                  />
                  <p className="text-xs text-muted-foreground">
                    Cuando el cliente responda al email, su respuesta irá a esta dirección.
                  </p>
                </div>
              </div>
            </div>

            {/* Opciones adicionales */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Opciones</h3>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="useTLS"
                  {...register('useTLS')}
                />
                <Label htmlFor="useTLS" className="text-sm font-normal cursor-pointer">
                  Usar TLS/SSL
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="enabled"
                  {...register('enabled')}
                />
                <Label htmlFor="enabled" className="text-sm font-normal cursor-pointer">
                  Habilitar envío de emails
                </Label>
              </div>
            </div>

            {/* Prueba de email */}
            <div className="space-y-4 pt-6 border-t border-border">
              <h3 className="text-lg font-semibold">Prueba de Email</h3>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="Email para prueba"
                  value={testingEmail}
                  onChange={(e) => setTestingEmail(e.target.value)}
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={onSubmitTestingEmail}
                  disabled={!testingEmail || emailConfigLoading}
                >
                  Enviar Prueba
                </Button>
              </div>
            </div>

            {/* Botones de acción */}
            <div className="flex justify-end items-center gap-3 pt-6 border-t border-border">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/settings')}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={emailConfigLoading}
                className="bg-black text-white hover:bg-neutral-800 dark:bg-white dark:text-black dark:hover:bg-white/90"
              >
                {emailConfigLoading ? 'Guardando...' : 'Guardar Configuración'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EmailConfigPage;
